<!-- eslint-disable vue/prefer-true-attribute-shorthand -->
<template>
  <ais-search-box
    :class-names="{
      'ais-SearchBox': 'header-search__search-box',
    }"
  >
    <template #default="{ refine, currentRefinement }">
      <form
        class="header-search__form"
        :action="searchAction"
        method="get"
        role="search"
        @submit="onSubmit"
      >
        <div
          class="header-search"
          :class="{ 'header-search--active': searchModalActive }"
        >
          <button
            class="header-search__expand"
            type="button"
            tabindex="-1"
            @click="openSearchModal($event, refine)"
            @mouseenter="prepareSearchModal(refine)"
            @touchstart="prepareSearchModal(refine)"
          >
            <span
              class="visually-hidden"
              v-text="$string('predictive_search.toggle')"
            />

            <icon-search />
          </button>

          <label
            class="visually-hidden"
            :for="inputId"
            v-text="$string('predictive_search.placeholder')"
          />

          <input
            :id="inputId"
            ref="searchRef"
            class="header-search__input text-body-2xs"
            type="text"
            name="query"
            autocomplete="off"
            :placeholder="$string('predictive_search.placeholder')"
            required
            :value="getValue(currentRefinement)"
            @focus="openSearchModal($event, refine)"
            @input="updateSearchQuery($event, refine)"
            @keydown.enter="onEnterPress"
          >

          <!--
            The visible placeholder is a real element so it can ease across on
            focus without moving the caret. The input keeps its own placeholder
            attribute for :placeholder-shown and assistive technology, hidden
            behind a transparent colour.
          -->
          <span
            aria-hidden="true"
            class="header-search__placeholder text-body-2xs"
            v-text="$string('predictive_search.placeholder')"
          />

          <button
            v-show="showClearButton"
            ref="searchClear"
            class="header-search__clear"
            type="button"
            @click="closeSearchModal(refine)"
          >
            <span
              class="visually-hidden"
              v-text="$string('predictive_search.close')"
            />

            <icon-close />
          </button>
        </div>
      </form>
    </template>
  </ais-search-box>
</template>

<script>

/**
 * Vue: Search form (search-form)
 * -----------------------------------------------------------------------------
 * Search form component.
 * - The field is exposed in the header at all breakpoints, selecting it opens
 *   the predictive search overlay.
 *
 */
 import { mapActions, mapGetters, mapState } from 'vuex'
import { AisSearchBox } from 'vue-instantsearch/vue3/es'

import IconSearch from '~icons/misc/search.svg'
import IconClose from '~icons/misc/close.svg'

import { debounce } from '~/helpers/general'
import { getSearchUrl } from '~/helpers/routes'

export default {
  name: 'SearchForm',

  components: {
    AisSearchBox,
    IconSearch,
    IconClose,
  },

  props: {
    view: {
      // 'desktop-form' or 'mobile-form'
      type: String,
      default: 'desktop-form',
    },
  },

  emits: ['update:query', 'focussed'],

  data() {
    return {
      input: '',
      touched: false,
      searchModalActive: false,
      userPressedEnter: false,
      blockReopenUntil: 0,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      settings: (state) => state.index.settings,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getOverlayIndex: 'overlays/getOverlayIndex',
    }),

    /**
     * Unique input id per view so the two header instances don't clash.
     * @returns {String}
     */
    inputId() {
      return `header-search-input-${this.view}`
    },

    namespace() {
      return this.view === 'desktop-form' ? 'search-modal' : 'search-modal search-modal-mobile'
    },

    showClearButton() {
      return this.searchModalActive
    },

    searchAction() {
      return getSearchUrl()
    },
  },

  watch: {

    /**
     * Watch active overlay to reset current link.
     */
    async getActiveOverlay() {
      if (this.getActiveOverlay?.namespace === this.namespace) {
        this.searchModalActive = true
        return
      }

      /**
       * Clear input when overlay closes.
       * - Closing restores focus to whatever opened the overlay, which is this
       *   field. Block the re-open that focus would otherwise trigger, and
       *   release focus so the field returns to its default state.
       * - Only when the overlay has actually closed. Another overlay opening
       *   over the top of it leaves it in the stack, and the field must stay
       *   able to bring it back.
       */
      if (this.searchModalActive && this.getOverlayIndex(this.namespace) < 0) {
        this.input = ''
        this.$emit('update:query', '')
        this.blockReopenUntil = Date.now() + this.settings.throttle + 400

        if (document.activeElement === this.$refs.searchRef) {
          this.$refs.searchRef.blur()
        }
      }

      this.searchModalActive = false
    },
  },

  created() {
    this.debouncedRefineResults = debounce(this.refineResults, this.settings.throttle)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    onSubmit(event) {
      // iOS autocomplete = submit WITHOUT Enter:
      if (!this.userPressedEnter) {
        event.preventDefault()
        return
      }

      // Reset after valid manual submit
      this.userPressedEnter = false
    },

    onEnterPress() {
      this.userPressedEnter = true
    },

    /**
     * Allows the search query to be updated from the parent.
     * @param {Event} event - Input event.
     * @param {Function} refine - Algolia refine function.
     */
    updateSearchQuery(event, refine) {
      const value = event?.currentTarget?.value || ''
      this.input = value
      this.touched = true

      this.debouncedRefineResults(refine, value)
      this.$emit('update:query', value)
    },

    /**
     * Allows the search query to be updated from the parent.
     * @param {Function} refine - Algolia refine function.
     * @param {String} query - Input field value.
     */
    refineResults(refine, query) {
      refine(query)
    },

    getValue(currentRefinement) {

      /**
       * Before interaction with search box, set input from currentRefinement
       * and fire event search-active
       */
      if (!this.touched && currentRefinement !== this.input) {
        this.input = currentRefinement
      }

      return this.input
    },

    async prepareSearchModal(refine) {
      // Don't prepare if modal is already active - this prevents clearing the query
      const isActive = this.getActiveOverlay?.namespace === this.namespace
      if (isActive) {
        return
      }

      this.$emit('focussed')

      refine('')
    },

    /**
     * Open the predictive search overlay from the exposed field.
     * - Fired by both the icon and focussing the input, so bail out when the
     *   overlay is already open to avoid clearing an in-progress query.
     * @param {Object} event - Click or focus event.
     * @param {Function} refine - Algolia refine function.
     */
    async openSearchModal(event, refine) {
      if (this.getActiveOverlay?.namespace === this.namespace) {
        return
      }

      /**
       * The overlay hands focus back to this field as it closes; that focus
       * must not re-open it. Release focus so the field settles at rest.
       */
      if (Date.now() < this.blockReopenUntil) {
        this.$refs.searchRef?.blur()
        return
      }

      this.$emit('focussed')

      // Clear query before opening to ensure trending products show
      this.input = ''
      refine('')
      this.$emit('update:query', '')

      /**
       * An overlay already in the stack is merged in place rather than moved,
       * so drop it first to guarantee it re-opens on top of anything the user
       * opened over it — the navigation drawer, typically.
       */
      if (this.getOverlayIndex(this.namespace) > -1) {
        this.closeOverlay(this.namespace)
      }

      /**
       * Opened on top of whatever is already open rather than clearing it, so
       * closing search from the navigation drawer returns to the drawer instead
       * of dismissing it.
       */
      this.openOverlay({
        component: 'search-modal',
        ignoreDismissed: true,
        namespace: this.namespace,
      })
    },

    /**
     * Clear search input and close search modal.
     * @param {Function} refine - Algolia refine function.
     */
    closeSearchModal(refine) {
      this.blockReopenUntil = Date.now() + this.settings.throttle + 400
      this.input = ''
      this.$emit('update:query', '')
      this.closeOverlay(this.namespace)
      setTimeout(() => refine(''), this.settings.throttle)

      /**
       * Release focus so the field returns to its default state rather than
       * re-opening the overlay.
       */
      this.$nextTick(() => {
        this.$refs.searchRef?.blur()
      })
    },
  },
}
</script>
